import { useState } from 'react';
import { api } from '../api';
import { haptic } from '../telegram';
import { Card, ErrorBanner } from '../components/common';

const ENVIRONMENTS = [
  { value: '', label: 'не указано' },
  { value: 'indoor', label: 'в помещении' },
  { value: 'balcony', label: 'на балконе' },
  { value: 'outdoor', label: 'на улице' },
  { value: 'greenhouse', label: 'в теплице' },
];

const LIGHT_LEVELS = [
  { value: '', label: 'не указано' },
  { value: 'low', label: 'мало света' },
  { value: 'medium', label: 'средний свет' },
  { value: 'bright_indirect', label: 'яркий рассеянный' },
  { value: 'direct_sun', label: 'прямое солнце' },
];

const DATE_HINTS = [
  { value: '', label: 'не знаю' },
  { value: 'today', label: 'сегодня' },
  { value: 'this_week', label: 'на этой неделе' },
  { value: 'month_ago', label: 'около месяца назад' },
  { value: 'this_season', label: 'в этом сезоне' },
];

function hintToDate(hint: string): { plantedAt: string | null; precision: string | null } {
  const days: Record<string, number> = { today: 0, this_week: 3, month_ago: 30, this_season: 60 };
  const precision: Record<string, string> = {
    today: 'exact',
    this_week: 'week',
    month_ago: 'month',
    this_season: 'season',
  };
  if (!(hint in days)) return { plantedAt: null, precision: null };
  const date = new Date(Date.now() - days[hint] * 86_400_000);
  return { plantedAt: date.toISOString(), precision: precision[hint] };
}

interface Props {
  spaceId: string;
  onCreated: () => void;
  onCancel: () => void;
}

export function AddPlant({ spaceId, onCreated, onCancel }: Props) {
  const [displayName, setDisplayName] = useState('');
  const [speciesText, setSpeciesText] = useState('');
  const [locationText, setLocationText] = useState('');
  const [environmentType, setEnvironmentType] = useState('');
  const [lightLevel, setLightLevel] = useState('');
  const [plantedHint, setPlantedHint] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [photo, setPhoto] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!displayName.trim()) {
      setError('Придумайте название — так проще отличать растения друг от друга');
      return;
    }
    setBusy(true);
    setError(null);
    const planted = hintToDate(plantedHint);
    try {
      const created = await api.createPlant(spaceId, {
        displayName: displayName.trim(),
        speciesText: speciesText.trim() || null,
        locationText: locationText.trim() || null,
        environmentType: environmentType || null,
        lightLevel: lightLevel || null,
        plantedAt: planted.plantedAt,
        plantedAtPrecision: planted.precision,
        notes: notes.trim() || null,
      });
      if (photo) {
        // Без фото растение всё равно добавлено — фото можно поставить позже в карточке.
        await api.uploadPlantPhoto(created.id, photo).catch(() => undefined);
      }
      haptic('success');
      onCreated();
    } catch (e) {
      haptic('error');
      setError(e instanceof Error ? e.message : 'Не удалось добавить растение');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <button className="btn secondary" onClick={onCancel} style={{ marginBottom: 12 }}>
        ← Назад
      </button>
      <h1>Новое растение</h1>

      {error ? <ErrorBanner message={error} /> : null}

      <Card>
        <div className="field">
          <label>Фото — чтобы быстрее находить в списке</label>
          <label className="photo-pick">
            {photo ? (
              <img src={URL.createObjectURL(photo)} alt="фото растения" />
            ) : (
              <span>📷 Сфотографировать или выбрать</span>
            )}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
            />
          </label>
        </div>
        <div className="field">
          <label>Название *</label>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Монстера в гостиной"
          />
        </div>
        <div className="field">
          <label>Вид</label>
          <input value={speciesText} onChange={(e) => setSpeciesText(e.target.value)} placeholder="монстера" />
        </div>
        <div className="field">
          <label>Где стоит</label>
          <input value={locationText} onChange={(e) => setLocationText(e.target.value)} placeholder="гостиная" />
        </div>
        <div className="field">
          <label>Условия</label>
          <select value={environmentType} onChange={(e) => setEnvironmentType(e.target.value)}>
            {ENVIRONMENTS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Освещение</label>
          <select value={lightLevel} onChange={(e) => setLightLevel(e.target.value)}>
            {LIGHT_LEVELS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Когда посажено</label>
          <select value={plantedHint} onChange={(e) => setPlantedHint(e.target.value)}>
            {DATE_HINTS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Заметки</label>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>

        <button className="btn" disabled={busy} onClick={submit}>
          {busy ? 'Сохраняю…' : 'Добавить'}
        </button>
      </Card>
    </div>
  );
}
